import { createStore } from "vuex";
import { CREATE_ACCOUNT, CHECK_OTP, LOGIN, RESET_PASSWORD, CHANGE_PASSWORD_LOGGED_IN_USER, CHANGE_PASSWORD_NOT_LOGGED_IN_USER } from "@/config/urls";

function resolveAndSetToken(commit: (mutation: string, payload?: unknown) => void) {
  const params = new URLSearchParams(window.location.search);
  const tokenFromUrl = params.get("token");
  if (tokenFromUrl) {
    commit("SET_TOKEN", tokenFromUrl);
    return;
  }
  const tokenFromStorage = localStorage.getItem("token");
  if (tokenFromStorage) {
    try {
      const token = JSON.parse(tokenFromStorage);
      commit("SET_TOKEN", token || tokenFromStorage);
    } catch {
      commit("SET_TOKEN", tokenFromStorage);
    }
  }
}

export default createStore({
  state: {
    token: "",
    registrationErrorMessage: "",
    loginErrorMessage: "",
    restartPasswordErrorMessage: "",
    changePasswordErrorMessage: "",
    authorizationToken: "",
    hideAuthToggleButtons: false,
    isLoading: {
      login: false,
      createAccount: false,
      checkOtp: false,
      resetPassword: false,
      changePassword: false,
    },
  },
  getters: {
    getRegistrationErrorMessage: (state) => {
      return state.registrationErrorMessage;
    },
    getLoginErrorMessage: (state) => {
      return state.loginErrorMessage;
    },
    getRestartPasswordErrorMessage: (state) => {
      return state.restartPasswordErrorMessage;
    },
    getChangePasswordErrorMessage: (state) => {
      return state.changePasswordErrorMessage;
    },
    isLoadingLogin: (state) => {
      return state.isLoading.login;
    },
    isLoadingCreateAccount: (state) => {
      return state.isLoading.createAccount;
    },
    isLoadingCheckOtp: (state) => {
      return state.isLoading.checkOtp;
    },
    isLoadingResetPassword: (state) => {
      return state.isLoading.resetPassword;
    },
    isLoadingChangePassword: (state) => {
      return state.isLoading.changePassword;
    },
    isAuthToggleHidden: (state) => {
      return state.hideAuthToggleButtons;
    },
    isTokenGUID: (state) => {
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(state.token);
    },
  },
  mutations: {
    SET_TOKEN(state, token) {
      state.token = token;
    },
    SET_AUTHORIZATION_TOKEN(state, authorizationToken) {
      state.authorizationToken = authorizationToken;
    },
    SET_HIDE_AUTH_TOGGLE_BUTTONS(state, hide: boolean) {
      state.hideAuthToggleButtons = hide;
    },
    SET_REGISTRATION_ERROR_MESSAGE(state, message) {
      state.registrationErrorMessage = message;
    },
    SET_LOGIN_ERROR_MESSAGE(state, message) {
      state.loginErrorMessage = message;
    },
    SET_RESTART_PASSWORD_ERROR_MESSAGE(state, message) {
      state.restartPasswordErrorMessage = message;
    },
    SET_LOADING_LOGIN(state, isLoading) {
      state.isLoading.login = isLoading;
    },
    SET_LOADING_CREATE_ACCOUNT(state, isLoading) {
      state.isLoading.createAccount = isLoading;
    },
    SET_LOADING_CHECK_OTP(state, isLoading) {
      state.isLoading.checkOtp = isLoading;
    },
    SET_LOADING_RESET_PASSWORD(state, isLoading) {
      state.isLoading.resetPassword = isLoading;
    },
    SET_LOADING_CHANGE_PASSWORD(state, isLoading) {
      state.isLoading.changePassword = isLoading;
    },
    SET_CHANGE_PASSWORD_ERROR_MESSAGE(state, message) {
      state.changePasswordErrorMessage = message;
    },
  },
  actions: {
    async createAccount({ commit }, payload) {
      commit("SET_LOADING_CREATE_ACCOUNT", true);
      commit("SET_REGISTRATION_ERROR_MESSAGE", "");
      try {
        resolveAndSetToken(commit);
        const registerWithPhone = Boolean(payload.registerWithPhone);
        const body: Record<string, unknown> = {
          firstName: payload.firstName,
          lastName: payload.lastName,
          acceptedTerms: payload.acceptedTerms,
          agreedNotifications: payload.agreedNotifications,
          isAdult: payload.isAdult,
          username: payload.username,
          registerWithPhone,
        };

        if (registerWithPhone) {
          body.phone = payload.phone;
        } else {
          body.email = payload.email;
        }

        if (payload.birthday) body.birthday = payload.birthday;
        if (payload.affiliate) body.affiliate = payload.affiliate;

        const response = await fetch(CREATE_ACCOUNT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Token: this.state.token,
          },
          body: JSON.stringify(body),
        });

        const data = await response.json();
        if (!response.ok) {
          if (response.status === 400 || response.status === 409) {
            throw {
              status: response.status,
              data,
            };
          }

          if (response.status === 401) {
            commit("SET_REGISTRATION_ERROR_MESSAGE", "Token je istekao");
          }

          if (response.status === 500) {
            commit("SET_REGISTRATION_ERROR_MESSAGE", "Serverska greška");
          }
          throw new Error("Error");
        }

        return data;
      } finally {
        commit("SET_LOADING_CREATE_ACCOUNT", false);
      }
    },
    async checkOtp({ commit }, payload: { uuid: string; otp: string }) {
      commit("SET_LOADING_CHECK_OTP", true);
      commit("SET_REGISTRATION_ERROR_MESSAGE", "");
      commit("SET_RESTART_PASSWORD_ERROR_MESSAGE", "");
      try {
        resolveAndSetToken(commit);
        const response = await fetch(CHECK_OTP, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Token: this.state.token,
          },
          body: JSON.stringify({
            uuid: payload.uuid,
            otp: payload.otp,
          }),
        });

        const data = await response.json();
        if (!response.ok) {
          if (response.status === 400 || response.status === 409) {
            throw {
              status: response.status,
              data,
            };
          }

          if (response.status === 401) {
            commit("SET_REGISTRATION_ERROR_MESSAGE", "Token je istekao");
            commit("SET_RESTART_PASSWORD_ERROR_MESSAGE", "Token je istekao");
          }

          if (response.status === 500) {
            commit("SET_REGISTRATION_ERROR_MESSAGE", "Serverska greška");
            commit("SET_RESTART_PASSWORD_ERROR_MESSAGE", "Serverska greška");
          }
          throw new Error("Error");
        }

        return data;
      } finally {
        commit("SET_LOADING_CHECK_OTP", false);
      }
    },
    async resetPassword({ commit }, payload) {
      commit("SET_LOADING_RESET_PASSWORD", true);
      commit("SET_RESTART_PASSWORD_ERROR_MESSAGE", "");
      try {
        resolveAndSetToken(commit);
        const response = await fetch(RESET_PASSWORD, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Token: this.state.token,
          },
          body: JSON.stringify({
            username: payload.username,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          if (response.status === 400 || response.status === 409) {
            throw {
              status: response.status,
              data,
            };
          }

          if (response.status === 401) {
            commit("SET_RESTART_PASSWORD_ERROR_MESSAGE", "Token je istekao");
          }

          if (response.status === 500) {
            commit("SET_RESTART_PASSWORD_ERROR_MESSAGE", "Serverska greška");
          }
          throw new Error("Error");
        }

        return data;
      } finally {
        commit("SET_LOADING_RESET_PASSWORD", false);
      }
    },
    async login({ commit }, payload) {
      commit("SET_LOADING_LOGIN", true);
      commit("SET_LOGIN_ERROR_MESSAGE", "");
      try {
        resolveAndSetToken(commit);
        const response = await fetch(LOGIN, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Token: this.state.token,
          },
          body: JSON.stringify({
            username: payload.username,
            password: payload.password,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          if (response.status === 400 || response.status === 409) {
            throw {
              status: response.status,
              data,
            };
          }

          if (response.status === 401) {
            commit("SET_LOGIN_ERROR_MESSAGE", "Token je istekao");
          }

          if (response.status === 500) {
            commit("SET_LOGIN_ERROR_MESSAGE", "Serverska greška");
          }
          throw new Error("Error");
        }

        return data;
      } finally {
        commit("SET_LOADING_LOGIN", false);
      }
    },
    async changePassword({ commit }, payload) {
      commit("SET_LOADING_CHANGE_PASSWORD", true);
      try {
        if (!this.state.authorizationToken) {
          throw {
            status: 401,
            data: [],
            message: "Authorization token nije potvrđen.",
          };
        }

        resolveAndSetToken(commit);

        const url = this.getters.isTokenGUID ? CHANGE_PASSWORD_NOT_LOGGED_IN_USER : CHANGE_PASSWORD_LOGGED_IN_USER;

        const headers: any = this.getters.isTokenGUID
          ? {
              "Content-Type": "application/json",
              authorization: this.state.token,
            }
          : {
              "Content-Type": "application/json",
              token: this.state.token,
            };

        const response = await fetch(url, {
          method: "POST",
          headers: headers,
          body: JSON.stringify({
            password: payload.password,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          if (response.status === 400 || response.status === 409) {
            throw {
              status: response.status,
              data,
            };
          }

          if (response.status === 401) {
            throw {
              status: 401,
              data: [],
              message: "Token je istekao",
            };
          }

          if (response.status === 500) {
            throw {
              status: 500,
              data: [],
              message: "Serverska greška",
            };
          }

          throw {
            status: response.status,
            data: [],
            message: "Došlo je do greške",
          };
        }

        return data;
      } catch (error: unknown) {
        // Ako je već objekat sa status i data, prosleđuje ga dalje
        if (error && typeof error === "object" && ("status" in error || "message" in error)) {
          throw error;
        }
        // Ako je neka druga greška (npr. network error), wrap-uje je
        const errorMessage = error instanceof Error ? error.message : "Došlo je do greške prilikom slanja zahteva";
        throw {
          status: 0,
          data: [],
          message: errorMessage,
        };
      } finally {
        commit("SET_LOADING_CHANGE_PASSWORD", false);
      }
    },
  },
  modules: {},
});
